"use client";

import { ArrowRight, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Status = "available" | "low" | "sold-out";
const products = [{
  name: "Direct Property Acquisition",
  description: "We purchase residential properties directly from homeowners, ensuring a seamless, transparent, and fair transaction process.",
  price: "Value",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/property_acquisition.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/property_acquisition.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/property_acquisition.webp-wvc-sizes",
  status: "available" as Status,
  stockNote: "Active",
  span: "md:col-span-5 md:row-span-2",
  aspect: "aspect-[4/5]"
}, {
  name: "Strategic Renovations",
  description: "Enhancing long-term value through thoughtful, high-quality improvements that revitalize homes and neighborhoods.",
  price: "Growth",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/home_renovation.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/home_renovation.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/home_renovation.webp-wvc-sizes",
  status: "available" as Status,
  stockNote: "Ongoing",
  span: "md:col-span-4",
  aspect: "aspect-square"
}, {
  name: "Rental Portfolio",
  description: "Acquiring and managing residential rental properties to provide stable, high-quality housing solutions.",
  price: "Stability",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/rental_portfolio.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/rental_portfolio.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/rental_portfolio.webp-wvc-sizes",
  status: "available" as Status,
  stockNote: "Expanding",
  span: "md:col-span-3",
  aspect: "aspect-square"
}, {
  name: "Investor Partnerships",
  description: "Building lasting relationships with community partners and investors focused on responsible, sustainable growth.",
  price: "Integrity",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/investor_partnership.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/investor_partnership.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/investor_partnership.png-wvc-sizes",
  status: "available" as Status,
  stockNote: "Open",
  span: "md:col-span-3",
  aspect: "aspect-square"
}, {
  name: "Community Impact",
  description: "Investing in the future of our neighborhoods by fostering responsible development and long-term value.",
  price: "Legacy",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/community_impact.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/community_impact.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/community_impact.png-wvc-sizes",
  status: "available" as Status,
  stockNote: "Core",
  span: "md:col-span-4",
  aspect: "aspect-square"
}, {
  name: "Sustainable Growth",
  description: "A commitment to responsible investing that balances financial performance with community well-being.",
  price: "Vision",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/sustainable_growth.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/sustainable_growth.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/sustainable_growth.webp-wvc-sizes",
  status: "available" as Status,
  stockNote: "Primary",
  span: "md:col-span-5",
  aspect: "aspect-[4/5]"
}];
export default function Focus() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="166" id="our-areas-of-focus" className="bg-background py-20 md:py-[7.5rem]">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div ref={headerRef} className={`grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-12 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="md:col-span-7">
            <div className="flex items-center gap-2.5 mb-6">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-lg bg-secondary opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-lg bg-secondary"></span>
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
                Our Investment Pillars · Est. 2024
              </span>
            </div>
            <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-[3.25rem] leading-[1.05] tracking-tight text-foreground">
              Architects of
              <br />
              <span className="italic text-secondary">lasting legacy.</span>
            </h2>
          </div>

          <div className="md:col-span-5 md:pt-4 flex flex-col justify-end">
            <p className="font-sans text-base leading-relaxed text-muted-foreground max-w-md">
              We bridge the gap between institutional rigor and personal integrity, focusing on residential real estate that builds long-term value for our partners and communities.
            </p>
            <Link to="/HomePage" className="group mt-6 inline-flex items-center gap-2 font-sans text-sm font-medium text-foreground hover:text-secondary transition-colors w-fit">
              <span className="border-b border-foreground/30 group-hover:border-secondary pb-0.5">
                Explore Our Approach
              </span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <div ref={gridRef} className={`mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 transition-all duration-700 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {products.map((product, i) => {
          return <article key={i} data-index={i} className={`group relative flex flex-col overflow-hidden rounded-md border border-border bg-card transition-all duration-500 hover:-translate-y-1.5 hover:shadow-md ${product.span}`}>
                <div className={`relative w-full overflow-hidden ${product.aspect}`}>
                  <img src={product.image} data-wvc-srcset={product.srcset} data-wvc-sizes={product.sizes} alt={product.name} loading="lazy" decoding="async" className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]`} />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-foreground/10 via-transparent to-transparent" />

                  <div className="absolute top-3 right-3">
                    <Badge variant="secondary" className={`flex items-center gap-1.5 backdrop-blur-sm font-mono text-[10px] uppercase tracking-wider py-1 px-2.5 bg-background/90 text-secondary border-secondary/30`}>
                      <span className={`inline-block h-1.5 w-1.5 rounded-lg bg-secondary`} />
                      <span>{product.stockNote}</span>
                    </Badge>
                  </div>
                </div>

                <div className="flex flex-1 flex-col p-5 md:p-6">
                  <h3 className={`font-serif font-default text-2xl md:text-[1.625rem] leading-tight text-foreground`}>
                    {product.name}
                  </h3>
                  <p className="mt-2 font-sans text-sm leading-relaxed text-muted-foreground line-clamp-2">
                    {product.description}
                  </p>

                  <div className="mt-5 flex items-center justify-between">
                    <span className="font-serif font-default text-xl text-secondary font-medium">
                      {product.price}
                    </span>
                    <Button size="sm" variant="default" className="group/btn h-9 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 px-2.5 md:group-hover:px-4 transition-all duration-300" aria-label={`Learn more about ${product.name}`}>
                      <Plus className="h-4 w-4 shrink-0" />
                      <span className="hidden md:inline-block max-w-0 overflow-hidden whitespace-nowrap opacity-0 md:group-hover:max-w-[120px] md:group-hover:opacity-100 transition-all duration-300 text-xs font-medium">
                        Learn More
                      </span>
                      <span className="md:hidden text-xs font-medium ml-1">
                        View
                      </span>
                    </Button>
                  </div>
                </div>
              </article>;
        })}
        </div>

        <div ref={footerRef} className={`mt-16 md:mt-20 flex flex-col items-center text-center transition-all duration-700 ${footerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <svg width="120" height="14" viewBox="0 0 120 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-secondary/60" aria-hidden="true">
            <path d="M2 7C8 2, 14 12, 20 7C26 2, 32 12, 38 7C44 2, 50 12, 56 7C62 2, 68 12, 74 7C80 2, 86 12, 92 7C98 2, 104 12, 110 7C114 4, 117 6, 118 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <p className="mt-5 font-serif font-default italic text-lg md:text-xl text-foreground/80">
            Integrity in every transaction, value in every home.
          </p>
        </div>
      </div>
    </section>;
}